<template lang="pug">
  .deals-page
    .deals-header(:class="{ dark: darkMode, fiveForFive: fiveForFive }")
      .main-container-wide
        .breadcrumbs-container(v-if="!$rogue.appContext")
          Breadcrumbs(:links="[{ name: 'Deals' }]")
        .header-contents
          .head-block(v-if="darkMode")
            img(alt="Matte Black November" src="https://assets.roguefitness.com/image/upload/v1636134902/UI/matte_black/matte-black-november-WHITE_iphu6j.svg" :width="200" :height="59")
            p.notification(@click="openNewsletterForm = true") {{ __('Get notified when new Hot Deals drop!') }}
          .head-block(v-else)
            h1 {{ __('Rogue Deals') }}
            p {{ __('Rogue deals provide a unique opportunity to find rare strength and conditioning products at a budget cost.') }}
          .deal-types
            .deal-type(v-for="(type, index) in dealTypes" :data-track="'mbn-banner-deals-page-' + type.key" :data-index="'deals-page-mbn-banner-' + index")
              .type-img
                img(v-if="darkMode" :alt="type.title" :src="'https://assets.roguefitness.com/UI' + type.darkImg.src" :height="type.darkImg.height" :width="type.darkImg.width")
                img(v-else :alt="type.title" :src="'https://assets.roguefitness.com/UI' + type.img.src" :height="type.img.height" :width="type.img.width")
              .spacer
              p {{ __(type.text) }}
              RogueButton(
                color="flat"
                :class="($route.query.deal_type || '').indexOf(type.urlText || type.key) !== -1 && 'selected'"
                :link="($route.query.deal_type || '').indexOf(type.urlText || type.key) !== -1 ? '/deals' : '/deals?deal_type=' + (type.urlText || type.key)"
                :disabled="!type.has"
                :stop="false"
              )
                span(:class="{ 'shop-label': type.has }") {{ type.has ? 'Shop' : 'No' }}
                |  {{ type.title }}
        deals-newsletter-form(v-if="openNewsletterForm" @close="openNewsletterForm = false")
    .deals-body.main-container-wide
      .deals-content(ref="dealsContent")
        .top-line
          span(v-if="isLoading")
          span(v-else v-html="__('<strong>Showing {start}-{end}</strong> of {total} Products', counts)")
          RogueButton.mobile-filter(@click="showFacets(true)") Filter
          .selects
            RogueSelect(:options="pageSizeOptions" :modelValue="pageSize" @update:modelValue="pageSize = $event")
            RogueSelect(:options="sortOptions" :modelValue="$route.query.sort" @update:modelValue="changeSort($event)")
        .filters-active-row(v-if="activeFilters.length > 0")
          .active-filters-title.font-weight-bold.font-size-s {{ __('FILTERED BY') }}
          FiltersActive.active-filters(:filters="filters" :activeFilters="activeFilters")
        .mobile-filter-overlay(
          :class="mobileFacetsOpen && 'show'"
          @click="showFacets(false)"
        )
        .deals-main
          .filters-col
            filters.facets(
              :filters="filters"
              :order="filterOrder"
              :loading="isLoading"
              :class="{ open: mobileFacetsOpen }"
              @closed="showFacets(false)"
            )
          .products-wrapper
            hover-card-placeholder(v-if='isLoading' v-for='index in placeholderCount' :key="`placeholder-${index}`")
            .no-content(v-else-if="!isLoading && filteredProducts.length === 0")
              h3 {{ __('No Results') }}
            hover-card(
              v-else
              v-for="(product, index) in filteredProducts"
              :product="product"
              :key="product.productId"
              :position="index"
              :fetchPriority="index === 0 ? 'high' : 'auto'"
              :preload="index < 8"
              :dealsLayout="true"
              @click="trackClick(product, index)"
              v-element-visibility="(isVisible) => onHoverCardVisibility(isVisible, product, index)"
            )
        Pagination.pagination(:page-number="currentPage" :total-pages="pageCount")
    .description(v-if='category && category.description && !loading')
      .bottom-content.main-container(v-html="category.description" v-translate="category.name")
    .renderBlockFooter(v-if="slots.footer")
      p.blank-column &nbsp;
      RenderBlock(:block="slots.footer" :product="{sku: category.name}")
</template>
<script setup lang="ts">
import { onMounted, onServerPrefetch, onBeforeUnmount, computed, ref, watch, defineAsyncComponent, useTemplateRef } from 'vue'
import { type LocationQueryRaw, useRoute, useRouter } from 'vue-router'
import { useHead, type ReactiveHead } from '@unhead/vue'
import { allowScroll, preventScroll } from 'sisu/js/body-scroll'
import { getCloudinaryUrl } from 'sisu/js/helpers'
import { useMQ } from 'sisu/js/mq'
import { vElementVisibility } from '@vueuse/components'
import metadata from '@/js/helpers/metadata'
import { getDealsMetaTags } from '@/js/pages/deals/deals-meta-tags'
import { useRogue } from '@/js/services/Rogue'
import { useCategoryStore } from '@/js/stores/categoryStore'
import { useHttpStore } from '@/js/stores/httpStore'
import { usePromoStore } from '@/js/stores/promoStore'
import { useReviewsStore } from '@/js/stores/reviewsStore'
import FiltersActive from '@/js/pages/category/components/filters-active.vue'
import Filters from '@/js/pages/category/components/filters.vue'
import { buildFacets, backfillCategoryAttribute, backfillShoeTypeAttribute, getAppliedValues } from '@/js/pages/category/helpers'
import HoverCard from '@/js/pages/category/components/hover-card.vue'
import HoverCardPlaceholder from '@/js/pages/category/components/hover-card-placeholder.vue'
import { updateFacetRoutes } from '@/js/pages/category/helpers/lib/update-facet-routes'
import Pagination from '@/js/pages/category/components/pagination.vue'
import { PLACEHOLDER_PRODUCTS } from '@/js/services/rfk/util'
import { shownPromoPrice } from '@/js/helpers/promo-price'
import { DEAL_TYPES, BLACK_FRIDAY_DEAL_TYPES, HOT_DEALS, type TDealsIndex, type IPublicCategoryProduct } from '@/js/pages/deals/dealTypes'
import type { IReflektionFacet, IReflektionProcessedProduct } from '@/js/services/rfk/types.ts'
import { CARD_COUNT } from '@/js/stores/productConstants'

const DealsNewsletterForm = defineAsyncComponent(() => import('@/js/pages/deals/deals-newsletter-form.vue'))

const DEALS_CACHE_KEY = 'deals-index'

const categoryStore = useCategoryStore()
const httpStore = useHttpStore()
const promoStore = usePromoStore()
const reviewsStore = useReviewsStore()
const rogue = useRogue()
const route = useRoute()
const router = useRouter()
const mq = useMQ()

type DealProduct = IPublicCategoryProduct & {
  facets?: { [key: string]: string[] },
  sortPrice?: number,
}

const filteredProducts = ref<DealProduct[]>([])
const dealTypes = ref(DEAL_TYPES)
const filters = ref<{ [key: string]: IReflektionFacet }>()
// Kept local rather than as a uiStore overlay - in the app, the router guard hides every overlay on
// navigation, which would close the drawer on the query change from picking the first filter
const mobileFacetsOpen = ref(false)

// Facets shown no matter what is selected
const BASE_FILTER_ORDER = ['promo_status', 'deal_type', 'category', 'deal_status', 'shipping', 'manufacturer', 'pb']

/**
 * Extra facets to surface once the customer has drilled into a top-level category, keyed by
 * the `category` facet value. Any of these with no values in the current result set is dropped 
 * by `buildFacets`, so a category whose deals don't carry the attribute simply doesn't get the facet.
 */
const CATEGORY_FILTERS: { [category: string]: string[] } = {
  'Apparel'                 : ['colorstandard', 'shirtsize'],
  'Shoes'                   : ['shoetype', 'shoesize', 'colorstandard'],
  'Straps, Wraps & Support' : ['colorstandard'],
  'Rogue Rigs & Racks'      : ['accessorytype', 'compatibility'],
}

// Extra facets slot in directly under the category facet they belong to
const CATEGORY_FILTER_INDEX = BASE_FILTER_ORDER.indexOf('category') + 1

// Every key we may ever facet on. Per-product facets are built from this so they stay valid
// as the customer moves between categories without rebuilding them on each route change.
const ALL_FILTER_KEYS = [...new Set([...BASE_FILTER_ORDER, ...Object.values(CATEGORY_FILTERS).flat()])]

const filterOrder = computed(() => {
  const extraFilters = [...new Set(
    getAppliedValues('category', route).flatMap((cat: string) => CATEGORY_FILTERS[cat] || [])
  )]

  if (!extraFilters.length) {
    return BASE_FILTER_ORDER
  }

  return [
    ...BASE_FILTER_ORDER.slice(0, CATEGORY_FILTER_INDEX),
    ...extraFilters,
    ...BASE_FILTER_ORDER.slice(CATEGORY_FILTER_INDEX),
  ]
})

const activeFilters = computed(() => {
  if (!filters.value) return []
  
  return Object.keys(route.query)
    .filter(key => filters.value![key])
    .map(key => {
      const facet = filters.value![key]
      const activeValues = getAppliedValues(key, route)
      
      return facet.value
        // 5 for 5 is applied by its URL alias, not its attribute code
        .map((val: any) => ({ ...val, urlKey: val.urlKey === 'black_friday_dropship' ? '5for5' : val.urlKey }))
        .filter((val: any) => activeValues.indexOf(val.urlKey) !== -1)
        .map((val: any) => {
          const query = { ...route.query, [key]: activeValues.filter((v: string) => v !== val.urlKey) }
          if (activeValues.length === 1) delete query[key]
          
          return { key, text: val.text, clearRoute: { query } }
        })
    })
    .flat()
})
const isLoading = ref(true)
const sortOptions = [
  { label: 'Featured', value: '' },
  { label: 'Price: Low to High', value: 'price.asc' },
  { label: 'Price: High to Low', value: 'price.desc' },
]
const counts = ref<{ start: number, end: number, total: number } | null>(null)
const pageCount = ref(1)
const currentPage = ref(1)
const placeholderCount = ref(['EUR', 'GBR', 'AUS'].includes(rogue.site.code) ? CARD_COUNT.small : CARD_COUNT.medium)
const pageSize = ref(80)
const pageSizeOptions = [
  { label: '20', value: 20 },
  { label: '40', value: 40 },
  { label: '80', value: 80 },
]
const darkMode = ref(false)
const fiveForFive = ref(false)
const openNewsletterForm = ref(false)
const products = ref<DealProduct[]>([])
const hotDealProducts = ref<DealProduct[]>([])
const sortOrders = ref<{ [p: string]: number[] }>({})
const dealsCategoryId = 9893
let impressionEventTemplate: any = {}

const dealsContentRef = useTemplateRef<HTMLElement>('dealsContent')

const activePromos = computed(() => promoStore.activePromos)
const lastUpdate = computed(() => promoStore.lastUpdate)
const categories = computed(() => categoryStore.categories)
const category = computed(() => categories.value[dealsCategoryId] || {})
const slots = computed(() => {
  return ((category.value.roguebuilder || {}).slots || {})
})

if (rogue.featureFlags.isOn('fiveforfive_enabled')) {
  dealTypes.value = BLACK_FRIDAY_DEAL_TYPES
  fiveForFive.value = true

  dealTypes.value
    .filter(type => type.key === 'black_friday_dropship')
    .forEach(type => {
      if (rogue.site.code === 'EUR') {
        type.img.src = '/matte_black/5-for-5-EU-Black.svg'
        type.darkImg.src = '/matte_black/5-for-5-EU-White.svg'
      } else if (rogue.site.code === 'GBR') {
        type.img.src = '/matte_black/5-for-5-Pound-Black.svg'
        type.darkImg.src = '/matte_black/5-for-5-Pound-White.svg'
      }
      type.text = type.text.replace('$5', rogue.currency(5, { hideFractionalUnits: true }))
    })
}

// Boneyard deals are only offered in the US
if (rogue.site.code !== 'USA') {
  dealTypes.value = dealTypes.value.filter(t => t.key !== 'rogue_boneyard')
}

darkMode.value = rogue.featureFlags.isOn('matte_black_enabled')

// Cache this product's facets (which may be different than attrs) so we can match them later
function buildCardFacets(product: DealProduct) {
  backfillCategoryAttribute([product])
  backfillShoeTypeAttribute([product])

  product.facets = {}
  const promo = activePromos.value[promoKey(product)]

  if (promo) {
    product.attributes.promo_status = promo.status
  } else {
    // A deal can leave the store after this card was built, and its old status would keep matching the status filter
    delete product.attributes.promo_status
  }
  // The pb facet reads promo_price first. Without it, it falls back to special_price, which hover-card overwrites with
  // the hot deal price only once a card renders, so a deal's price range would depend on whether its card was drawn.
  product.attributes.promo_price = lowestPrice(product)

  buildFacets(ALL_FILTER_KEYS, [product], rogue).forEach(facet => {
    product.facets![facet.key] = product.facets![facet.key] || []
    facet.value.map(val => product.facets![facet.key].push(val.urlKey))
  })
}

function rebuildFacets() {
  products.value?.forEach(buildCardFacets)
}

// Shared so og:image and the CollectionPage JSON-LD can't drift apart
const dealsOgImage = computed(() => getCloudinaryUrl(category.value.meta?.iconPath || products.value[0]?.thumbnail, 1200, 630))

function buildHeadInput(): ReactiveHead {
  // Only the page's own breadcrumb label is used as a last resort - no invented copy
  const baseTitle = rogue.__(category.value.meta?.title || category.value.name || 'Deals')
  const { title, canonical } = getDealsMetaTags(route.query, rogue, dealTypes.value, baseTitle)

  let description = category.value.meta?.description || category.value.description
  if (description) {
    description = rogue.__(description, category.value.name)?.replace(/<[^>]*>/gi, '')
  }

  const image = dealsOgImage.value

  return metadata({
    url         : canonical,
    title,
    description,
    image,
    imageWidth  : image ? 1200 : undefined,
    imageHeight : image ? 630 : undefined,
    imageAlt    : image ? title : undefined,
  }, rogue) as ReactiveHead
}

useHead(() => buildHeadInput())

// Supporting signal only - no Google rich result attaches
// to CollectionPage. Skipped on filtered views, which are noindex anyway.
const collectionPageLdJson = computed(() => {
  if (!products.value.length) {
    return null
  }

  const { canonical, title } = getDealsMetaTags(route.query, rogue, dealTypes.value, rogue.__(category.value.meta?.title || category.value.name || 'Deals'))

  // No canonical means a filtered view, which isn't a page worth describing as a collection
  if (!canonical) {
    return null
  }

  return {
    '@context'         : 'https://schema.org',
    '@type'            : 'CollectionPage',
    name               : title,
    url                : canonical,
    primaryImageOfPage : dealsOgImage.value ? {
      '@type' : 'ImageObject',
      url     : dealsOgImage.value,
      width   : 1200,
      height  : 630,
    } : undefined,
    mainEntity: {
      '@type'         : 'ItemList',
      itemListElement : filteredProducts.value
        .filter(product => product.url)
        .map((product, i) => ({
          '@type'  : 'ListItem',
          position : i + 1,
          url      : `${rogue.localizedBaseURL}${product.url}`,
        })),
    },
  }
})

useHead({
  script: computed(() => (collectionPageLdJson.value ? [
    {
      type      : 'application/ld+json',
      key       : 'collection-page-ld-json',
      innerHTML : JSON.stringify(collectionPageLdJson.value),
    },
  ] : [])),
})

/**
 * Restricts the category facet to the shop menu's top-level categories. Deals in any other
 * category (e.g. Brands) still show in the grid; they just can't be filtered to by category.
 * Fails open (keeps every value) when the menu hasn't loaded. Returns undefined when no
 * value remains so the facet is not rendered.
 */
function allowlistCategories(facet?: IReflektionFacet): IReflektionFacet | undefined {
  if (!facet) return undefined

  const menuNames = categoryStore.menuTopLevelNames
  if (!menuNames.size) return facet

  const value = facet.value.filter(val => menuNames.has(val.urlKey))

  if (!value.length) return undefined

  return {
    ...facet,
    value,
    total              : value.length,
    number_of_products : value.reduce((sum, val) => sum + val.count, 0),
  }
}

// Only views that include Hot Deals swap cards: a feed card also stands for its other deal types (e.g. a Closeout & Sale default child).
function rebuildHotDealProducts() {
  hotDealProducts.value = products.value.map(product => {
    const hotDealCard = promoteActiveChild(product)

    if (hotDealCard !== product) buildCardFacets(hotDealCard)

    return hotDealCard
  })
}

function showFacets(show: boolean) {
  if (show) {
    rogue.track.track({ type: 'overlay', overlay: 'mobileFilters' })
  }
  mobileFacetsOpen.value = show
}

function trackView(product: IReflektionProcessedProduct, index: number) {
  if (rogue.ssr || !impressionEventTemplate) {
    return
  }

  const rfkProducts = [{ product, index }]

  rogue.track.listItemViewDebounce(Object.assign({}, impressionEventTemplate, { rfkProducts }))
}

function trackClick(product: IReflektionProcessedProduct, index: number) {
  rogue.track.track({
    type       : 'productClick',
    product,
    listType   : 'category',
    // @ts-expect-error category type doesn't match
    listEntity : category.value,
    page       : currentPage.value,
    position   : index + 1,
  })
}

function onHoverCardVisibility(isVisible: boolean, product: IReflektionProcessedProduct, index: number) {
  if (isVisible) {
    trackView(product, index)
  }
}

function changeSort(sortBy: string) {
  const newQuery: LocationQueryRaw = Object.assign({}, route.query, { page_number: undefined, sort: sortBy })
  router.push({ query: newQuery })
}

// Must match hover-card's promoId, or the missed-deal sort and filter disagree with what the card shows.
function promoKey(p: DealProduct) {
  if (!p.variationKey) return p.childId ?? p.productId

  return activePromos.value[p.productId]?.variations?.some(v => v.key === p.variationKey) ? p.productId : parseInt(p.variationKey, 10)
}

type RelatedProduct = NonNullable<IPublicCategoryProduct['related']>[number]

// /deals points a configurable's card at its default child (or the parent) even when only another child's deal is active; only /hot/active knows which.
function promoteActiveChild(product: DealProduct): DealProduct {
  if (product.variationKey) return product

  const ownStatus = activePromos.value[product.childId ?? product.productId]?.status
  if (ownStatus === 'active' || ownStatus === 'upcoming') return product
  // A parent card is only swapped when /hot/active has no entry for it (EDEV-3056 drops parents whose default child isn't a deal).
  if (!product.childId && ownStatus) return product
  // Outside 5for5, /hot/active drops missed deals, so an absent hot deal child is a missed one.
  if (!ownStatus && !product.attributes?.rogue_promo_enabled) return product

  const promotedProduct = product.related?.reduce<RelatedProduct | undefined>((bestCandidate, relatedProduct) => {
    const isCandidate = relatedProduct.childId && relatedProduct.childId !== product.childId && !relatedProduct.variationKey
    const promo = isCandidate ? activePromos.value[relatedProduct.childId as number] : undefined
    
    if (promo?.status !== 'active') return bestCandidate

    return !bestCandidate || promo.price < activePromos.value[bestCandidate.childId as number].price ? relatedProduct : bestCandidate
  }, undefined)
  
  if (!promotedProduct) {
    const parentPromo = activePromos.value[product.productId]
    const hasActiveSibling = parentPromo?.childrenIds?.some(id => id !== product.childId && activePromos.value[id]?.status === 'active')

    // No swatch holds an active deal (e.g. a size-only shirt), so the card falls back to the product, whose page opens with no size picked
    return product.childId && parentPromo?.status === 'active' && hasActiveSibling
      ? { ...product, childId: undefined, sku: product.groupSku ?? product.sku, url: product.url.split('?')[0], attributes: { ...product.attributes } }
      : product
  }

  const { related, position, ...missedProduct } = product
  const otherRelated = (related || []).filter(relatedProduct => relatedProduct !== promotedProduct)

  return {
    ...product,
    ...promotedProduct,
    productId     : product.productId,
    position,
    // The feed card's special_price is its default variant's sale, not the promoted variant's
    special_price : promotedProduct.special_price,
    // Copied so building this card's facets doesn't write into the related entry the feed card still shows
    attributes    : { ...(promotedProduct.attributes ?? product.attributes) },
    // A parent card (no childId) is the product's default, not a hot deal variant, so it doesn't become a swatch
    related       : product.childId ? [missedProduct, ...otherRelated] : otherRelated,
  } as DealProduct
}

// Mirrors price.vue's specialPrice so the sort order matches the price shown on each card.
function lowestPrice(p: DealProduct) {
  const price = Number(p.price)
  const promo = p.variationKey
    ? activePromos.value[p.productId]?.variations?.find(v => v.key === p.variationKey) || activePromos.value[parseInt(p.variationKey, 10)]
    : activePromos.value[p.childId ?? p.productId]

  // Server and browser must agree on this price, or the browser re-sorts the server-rendered cards after hydration.
  const showsMissedDeal = activePromos.value[promoKey(p)]?.status === 'missed'
  const discounts = [shownPromoPrice(promo, showsMissedDeal), promoStore.customerPrices[p.productId], p.special_price]
    // special_price is null on most products, and Number(null) would read as a $0 price.
    .filter(val => val === 0 || !!val)
    .map(Number)
    .filter(val => val < price)

  return Math.min(price, ...discounts)
}

function update() {
  if (isLoading.value) {
    return
  }

  const matchQuery = (key: string, product: DealProduct) => {
    const qsVal = route.query[key]
    if (qsVal === undefined) {
      return true
    }

    const toCheck = Array.isArray(qsVal) ? qsVal : [qsVal]

    return toCheck
      .map(val => (val === '5for5' ? 'black_friday_dropship' : val))
      .some(val => (product.facets![key] || []).indexOf(val as string) !== -1)
  }

  const sort = route.query.sort || 'featured'

  const activeFilterKeys = filterOrder.value.filter(attr => route.query[attr] !== undefined)
  const dealType = dealTypes.value.find(t => route.query.deal_type && (route.query.deal_type === t.key || route.query.deal_type === t.urlText))
  const productOrder: number[] = ((dealType?.key && sortOrders.value[dealType?.key]) || []).concat(sortOrders.value?.default)

  if (!rogue.ssr) {
    impressionEventTemplate = {
      type        : 'listView',
      listType    : 'category',
      query       : route.query.q,
      rfkProducts : [],
      filters     : activeFilterKeys.map(key => ({ type: key, value: route.query[key] })),
      listEntity  : {
        type : 'category',
        name : category.value.name,
        id   : category.value.categoryId,
        url  : category.value.url,
      },
    }
  }

  const appliedDealTypes = getAppliedValues('deal_type', route)
  // With no Deal Type applied every deal is listed, so a hot deal card must match what the Hot Deals view shows for it
  const viewProducts = !appliedDealTypes.length || appliedDealTypes.includes(HOT_DEALS.key) ? hotDealProducts.value : products.value
  const matchingProducts = viewProducts
    .filter(p => activeFilterKeys.every(attr => matchQuery(attr, p)))

  matchingProducts.forEach(p => { p.sortPrice = lowestPrice(p) })
  matchingProducts.sort((a, b) => {
    if (sort === 'price.asc' && a.sortPrice && b.sortPrice) {
      return a.sortPrice - b.sortPrice
    }
    if (sort === 'price.desc' && a.sortPrice && b.sortPrice) {
      return b.sortPrice - a.sortPrice
    }

    return productOrder.indexOf(a.productId) - productOrder.indexOf(b.productId)
  })

  const deferredPromos = matchingProducts.filter(p => {
    const promo = activePromos.value[promoKey(p)]

    return promo && promo.status !== 'active'
  })

  // Move promos that aren't buyable right now to the end, upcoming ahead of missed
  const upcoming: DealProduct[] = []
  const missed: DealProduct[] = []
  deferredPromos.forEach(p => {
    matchingProducts.splice(matchingProducts.indexOf(p), 1)
    ;(activePromos.value[promoKey(p)]?.status === 'upcoming' ? upcoming : missed).push(p)
  })
  matchingProducts.push(...upcoming, ...missed)

  currentPage.value = parseInt(route.query.page_number as string || '1', 10)
  const first = (currentPage.value - 1) * pageSize.value

  filteredProducts.value = matchingProducts.slice(first, first + pageSize.value)

  const skus = filteredProducts.value.map(p => p.sku)
  reviewsStore.loadSummaries(skus, rogue)

  pageCount.value = Math.ceil(matchingProducts.length / pageSize.value)

  counts.value = {
    start : first + 1,
    end   : first + filteredProducts.value.length,
    total : matchingProducts.length,
  }

  const currentFilters: { [key: string]: IReflektionFacet } = {}
  filterOrder.value.forEach(filter => {
    if (filter === 'deal_type' || route.query[filter]) {
      const matchesOtherFilters = (p: DealProduct) => filterOrder.value.filter(f => f !== filter).every(attr => matchQuery(attr, p))
      // Each deal type counts the cards its own view shows: Hot Deals the swapped cards, every other type the feed's
      const productsToUseForFacet = filter === 'deal_type'
        ? [
          ...products.value.filter(matchesOtherFilters).map(p => ({ ...p, attributes: { ...p.attributes, [HOT_DEALS.key]: false } })),
          ...hotDealProducts.value.filter(matchesOtherFilters).map(p => ({ ...p, attributes: { [HOT_DEALS.key]: p.attributes[HOT_DEALS.key] } })),
        ]
        : viewProducts.filter(matchesOtherFilters)

      currentFilters[filter] = buildFacets([filter], productsToUseForFacet, rogue)[0]
    } else {
      currentFilters[filter] = buildFacets([filter], matchingProducts, rogue)[0]
    }

    if (filter === 'category') {
      const categoryFacet = allowlistCategories(currentFilters[filter])

      if (categoryFacet) {
        currentFilters[filter] = categoryFacet
      } else {
        delete currentFilters[filter]
      }
    }
  })

  updateFacetRoutes(currentFilters, route.query)

  let changed = false
  const newFilters: LocationQueryRaw = JSON.parse(JSON.stringify(route.query))
  Object.entries(route.query)
    .filter(([key]) => ALL_FILTER_KEYS.includes(key))
    .forEach(([key, appliedVals]) => {
      // A category-specific facet whose category is no longer selected doesn't filter
      // anything, so drop it from the URL rather than leaving a dead param behind
      if (!filterOrder.value.includes(key)) {
        delete newFilters[key]
        changed = true

        return
      }

      appliedVals = Array.isArray(appliedVals) ? appliedVals : [appliedVals]
      const validVals = appliedVals.filter(val => currentFilters[key]?.value.some(f => f.urlKey === val || (f.urlKey === 'black_friday_dropship' && val === '5for5')))
      if (appliedVals.length !== validVals.length) {
        changed = true
      }

      // An empty array would stay in route.query and match no products, so drop the key instead
      if (validVals.length) {
        newFilters[key] = validVals
      } else {
        delete newFilters[key]
      }
    })

  if (changed) {
    router.replace({ query: newFilters })
  }

  filters.value = currentFilters

  // Custom manufacturer sorting
  if (filters?.value?.manufacturer) {
    const customManufacturerOrder = ['Rogue Fitness', 'OSO', 'Westside Barbell', 'Ghost', 'Reflect Fitness']

    filters.value.manufacturer.value.sort((a, b) => {
      const indexA = customManufacturerOrder.indexOf(a.text)
      const indexB = customManufacturerOrder.indexOf(b.text)

      if (indexA === -1) return 1
      if (indexB === -1) return -1

      return indexA - indexB
    })
  }
}

async function load() {
  const cachedResp = !rogue.ssr ? httpStore.cache[DEALS_CACHE_KEY] as TDealsIndex | undefined : undefined

  // Revisits (back button, or hydrating the server render) already have everything loaded. Apply it synchronously so
  // the products render straight away - swapping in placeholders first collapses the page, and the browser can't
  // restore the scroll position on a page that short
  if (cachedResp && categories.value[dealsCategoryId] && categoryStore.menuItems.length) {
    applyDeals(cachedResp)

    return
  }

  isLoading.value = true

  // Backs the category facet allowlist. Loads alongside the feed and fails open if it can't load.
  const menuPromise = categoryStore.loadMenu(rogue).catch(() => {})

  if (!categories.value[dealsCategoryId]) {
    await categoryStore.load(dealsCategoryId, rogue)
  }

  let dealsResp: TDealsIndex
  if (cachedResp) {
    dealsResp = cachedResp
  } else {
    // Promo statuses back the promo_status facet, so they must be loaded before the first update()
    // or a promo_status filter in the URL (e.g. on refresh) matches nothing and is stripped
    [dealsResp] = await Promise.all([
      rogue.fetch<TDealsIndex>('/svc_content/deals'),
      promoStore.refresh(rogue),
    ])
    // Cached in the browser too, so coming back to the page from a client-side visit renders without refetching
    httpStore.setCache(DEALS_CACHE_KEY, dealsResp)
  }

  await menuPromise

  applyDeals(dealsResp)
}

function applyDeals(dealsResp: TDealsIndex) {
  const productIds = dealsResp.products.map(p => p.productId)
  products.value = dealsResp.products
    .filter((p, i) => productIds.indexOf(p.productId) === i)
  sortOrders.value = dealsResp.sorts || {}
  sortOrders.value.default = []

  rebuildFacets()
   
  products.value.forEach(p => {
    const entityId = p.productId
    sortOrders.value.default.push(entityId)

    if (p.related && p.related.length) {
      p.related.forEach(r => {
        // If we only received the thumbnail and productId on the related object in the deals response,
        // Get additional properties for the related product from the root level of the deals reponse.
        if (!r.price) {
          const match = dealsResp.products.find(pr => pr.productId === r.productId)
          if (!match) return
          const { price, special_price, url, sku, name } = match
          r = Object.assign(r, { price, special_price, url, sku, name })
        }
      })
    }

    if (PLACEHOLDER_PRODUCTS[entityId]) {
      p.productId = PLACEHOLDER_PRODUCTS[entityId].productId
      sortOrders.value.default.push(p.productId)
      p.url = PLACEHOLDER_PRODUCTS[entityId].url
    }
  })

  rebuildHotDealProducts()

  dealTypes.value.forEach(type => {
    type.has = products.value.some(p => p.attributes[type.key])
  })
  isLoading.value = false
  update()
}

onServerPrefetch(() => load())

if (!rogue.ssr) {
  load()
}

onMounted(() => {
  rogue.log.debug('Loading deals')
  promoStore.refresh(rogue)
  if (localStorage.getItem('deals-page-size')) {
    pageSize.value = parseInt(localStorage.getItem('deals-page-size') || '80', 0)
  }
})

watch(mobileFacetsOpen, open => {
  if (open) {
    preventScroll()
  } else {
    allowScroll()
  }
})

onBeforeUnmount(() => {
  if (mobileFacetsOpen.value) {
    allowScroll()
  }
})

watch(() => route.query, () => {
  // If mobile filters are open, we don't want to run this
  // The customer should be able to select/deselect multiple
  // filters before the update runs in the `mobileFacetsOpen` watcher
  update()
  if (dealsContentRef.value && dealsContentRef.value.getBoundingClientRect().top < 0) {
    dealsContentRef.value.scrollIntoView({
      block: 'start', inline: 'start', behavior: mq.value === 'mobile' ? 'smooth' : 'auto',
    })
  }
}, {
  immediate: false,
})

// Whenever the promo store updates, we may need to reorder products
watch(lastUpdate, () => {
  // The promo status may have changed
  rebuildFacets()
  rebuildHotDealProducts()
  // We may need to reorder products
  update()
})

watch(pageSize, (newPageSize: number) => {
  localStorage.setItem('deals-page-size', newPageSize.toString())
  update()
  if (pageCount.value < currentPage.value) {
    rogue.router?.push({ query: undefined })
  }
})
</script>
<style lang="scss" scoped>
.notification {
  cursor: pointer;
  margin-top: $space-m;
  text-align: center;
  color: $color-white;
  text-decoration: underline;
}

.mobile-filter {
  padding: 14px $space-m;
  min-width: 100px;
}

.mobile-filter-overlay {
  position: fixed;
  opacity: 0;
  pointer-events: none;
  background-color: rgba(0, 0, 0, 0.3);
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;

  &.show {
    opacity: 1;
    pointer-events: all;
  }
}

.selects {
  display: none;
}

.header-contents {
  padding-bottom: $space-s;

  .deal-type {
    text-align: center;

    .type-img,
    p {
      display: none;
    }
  }
}

.top-line {
  font-size: $size-xs;
  display: flex;
  margin-bottom: $space-s;
  align-items: center;
  justify-content: space-between;
}

.active-filters{
  margin-bottom: $space-s;
  width: 100%;
}

.filters-active-row {
  @include all-desktop() {
    display: flex;

    .active-filters-title {
      width: 200px;
      margin-right: $space-l;
      flex-shrink: 0;
      padding-top: $space-s;
    }

    :deep(.filtered-by) {
      display: none;
    }
  }

  @include not-desktop() {
    .active-filters-title {
      display: none;
    }
  }
}

.deals-main {
  display: flex;
  align-items: start;
}

.filters-col{
  @include all-desktop() {
    flex-basis: 200px;
    flex-grow: 0;
    flex-shrink: 0;
    margin-right: 32px;
  }
}

.facets {
  align-self: normal;
  
  @include not-desktop() {
    position: fixed;
    top: 0;
    left: calc(-100% - 24px);
    z-index: 2;
    height: 100vh;
    width: 100%;
    max-width: 350px;
    transition: left 500ms ease-in-out;

    &.open {
      left: 0;
    }
  }

  @include all-desktop() {
    height: 100%;
  }
}

.products-wrapper {
  display: flex;
  flex-wrap: wrap;
  flex-grow: 2;
  min-width: 0;

  &>div {
    flex-basis: 50%;

    @media screen and (min-width: 480px) {
      flex-basis: 33.333%;
    }

    @media screen and (min-width: 960px) {
      flex-basis: 25%;
    }
  }
}

.pagination {
  margin-bottom: $space-m;
}

@include all-desktop() {
  .deals-page .top-line .mobile-filter {
    display: none;
  }

  .selects {
    display: flex;
    margin-bottom: 0;
    height: 54px;

    >:first-child {
      margin-right: $space-xs;
    }
  }

  .header-contents {
    display: flex;
  }

  .head-block {
    flex-basis: 200px;
    flex-grow: 0;
    flex-shrink: 0;
    margin-right: 32px;

    p {
      font-size: $size-xs;
    }
  }

  .pagination {
    margin-left: 232px;
  }
}

.breadcrumbs-container {
  min-height: $size-xxs;
  margin-bottom: $space-s;

  @media(min-width: $desktop) {
    margin-bottom: $space-m;
  }
}

.deals-header {
  padding-top: $space-s;

  &.dark {
    background: $color-black;
    color: $color-white;
    margin-bottom: $space-s;

    .head-block {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      img {
        width: 100%;
        max-width: 200px;
      }

      p {
        font-size: $size-xxs;
      }
    }

    :deep(.crumb-link) {
      color: $color-gray;

      &:hover {
        color: $color-white;
      }
    }

    .button {
      background: $color-black;
      color: $color-white;

      &:hover,
      &.selected {
        color: $color-black;
        border-color: $color-white;
        background-color: $color-white;
      }
    }
  }
}

.deal-types {
  display: flex;
  width: 100%;
  margin: 0 auto;

  @include all-desktop() {
    overflow-x: hidden;
    flex-wrap: nowrap;
    // Boneyard logo should stick out the top
    margin-top: -20px;
    padding: 0 4px 4px 4px;

    .deal-type {
      display: flex;
      flex-direction: column;
      margin-right: $space-s;
      flex: 1;

      &:last-child {
        margin-right: 0;
      }

      .spacer {
        flex-grow: 1;
      }

      .type-img,
      p {
        display: block;
      }

      .type-img {
        height: 66px;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: $space-s;
      }

      img {
        display: block;
        max-width: 100%;
      }

      p {
        font-size: $size-xxs;
      }

      .button {
        justify-self: flex-end;
      }
    }
  }

  @include desktop() {
    padding-top: $space-s;
  }

  .deal-type {
    margin-left: -1px;

    &:first-child {
      margin-left: 0;
    }
  }

  .button {
    font-weight: $weight-bold;
    text-transform: uppercase;
    white-space: nowrap;
    font-family: $font-condensed;
    padding: $space-xs;

    &:hover {
      border-color: black;
      border-width: 1px;
      z-index: 1;
    }
  }

  @include not-desktop() {
    justify-content: center;
    gap: $space-xxs;
    padding-bottom: $space-xxxs;

    .deal-type {
      display: flex;
      flex: 1 1 auto;
      margin-left: 0;
    }

    .button {
      width: 100%;
    }

    .shop-label {
      display: none;
    }
  }

  @include mobile {
    flex-wrap: wrap;

    .deal-type {
      flex: 0 0 calc(50% - #{$space-xxs} / 2);
    }

    .deal-type:first-child:nth-last-child(5) ~ .deal-type:nth-child(n + 3),
    .deal-type:first-child:nth-last-child(3),
    .deal-type:first-child:nth-last-child(3) ~ .deal-type {
      flex: 1 1 auto;
    }

    .button {
      font-size: $size-xs;
      padding: $space-xs $space-xxxs;

      @media (max-width: 359px) {
        font-size: $size-xxs;
        padding: $space-xs 0;
      }
    }
  }
}

@include tablet-and-small-desktop() {
  .header-contents {
    display: block;
  }
}

.description {
  display: flex;
  justify-content: center;
  background-color: $color-gray-x-light;
  padding: $space-xxl $space-s;
  position: relative;
  clear: both; // no grid support fallback
}

.bottom-content {
  max-width: $tablet;
  text-align: center;
}

.renderBlockFooter {
  display: flex;
  justify-content: center;
  background-color: $color-gray-x-light;
  padding: $space-xxl $space-s;
  text-align: justify;
  > div {
    width: 100%;
    max-width: $desktop;
    flex-grow: 1;
  }

  p.blank-column {
    flex-shrink: 0;
    flex-grow: 0;
    flex-basis: 240px;

    @include not-desktop() {
      display: none;
    }
  }
}
</style>
